/**
 * entities/notification/api/useNotificationPreferences — Email opt-in preference hooks.
 *
 * Wraps GET /api/auth/preferences/ and PATCH /api/auth/preferences/ using the
 * same cache key `['preferences']` as features/account-settings/api/usePreferences.ts.
 *
 * SAME cache key = shared TanStack Query cache = single source of truth:
 *   - Toggling email opt-in in the notification popup (AC4 widget 07) is
 *     immediately reflected in the Settings page preferences form (AC4 widget 08).
 *   - Toggling in Settings is immediately reflected in the popup.
 *   - No duplicate network requests when both are mounted.
 *
 * FSD: entities may import from shared only.  Uses the shared createGetQuery
 * and createPatchMutation factories from shared/api (the established pattern).
 */

import { useQueryClient } from '@tanstack/react-query';
import { createGetQuery, createPatchMutation } from '@/shared/api';

/**
 * Shared cache key — MUST match features/account-settings/api/usePreferences.ts.
 * Sharing this key means both the notification popup and the Settings form
 * read from and write to the same TanStack Query cache entry.
 */
export const PREFERENCES_QUERY_KEY = ['preferences'] as const;

/**
 * Read the current user's preferences.
 *
 * Shares cache with features/account-settings/api/usePreferences.ts so a
 * single GET /api/auth/preferences/ request services both consumers.
 */
export const useNotificationPreferencesQuery = createGetQuery(
  '/api/auth/preferences/',
  PREFERENCES_QUERY_KEY,
);

/** Optional per-call callbacks for {@link useUpdateEmailOptInMutation}'s `mutate`. */
interface UpdateEmailOptInCallbacks {
  onError?: (error: Error) => void;
}

/**
 * Update the `email_opt_in` flag (or any other preference field).
 *
 * Invalidates the shared preferences cache on success so the Settings page
 * and the notification dropdown stay in sync. Callers may pass `onError` to
 * surface failures (e.g. a destructive toast) without losing the built-in
 * cache invalidation on success.
 *
 * Usage:
 *   const { mutate } = useUpdateEmailOptInMutation();
 *   mutate({ email_opt_in: true }, { onError: (error) => toast(...) });
 */
export function useUpdateEmailOptInMutation() {
  const queryClient = useQueryClient();

  const mutation = createPatchMutation('/api/auth/preferences/')();

  return {
    ...mutation,
    mutate: (
      body: Parameters<typeof mutation.mutate>[0],
      callbacks?: UpdateEmailOptInCallbacks,
    ) => {
      mutation.mutate(body, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY });
        },
        onError: callbacks?.onError,
      });
    },
    mutateAsync: async (body: Parameters<typeof mutation.mutateAsync>[0]) => {
      const result = await mutation.mutateAsync(body);
      await queryClient.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY });
      return result;
    },
  };
}
